import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { LOGO, CONTACT, SERVICES_DATA } from '../data/siteData';

const Footer = () => {
  const { lang } = useLang();
  const t = translations[lang].footer;
  const tNav = translations[lang].nav;
  const services = SERVICES_DATA[lang];

  return (
    <footer className="bg-[#0A0A0C] border-t border-white/5" data-testid="footer">
      <div className="container py-20 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Logo & Tagline - Original colors */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO} 
              alt="Imperguel" 
              className="h-10 w-auto mb-6" 
              data-testid="footer-logo" 
            />
            <p className="text-sm text-white/50 leading-relaxed mb-6 font-light">
              {t.tagline}
            </p>
            
            {/* Social Icons */}
            <div className="flex items-center gap-4" data-testid="footer-social">
              <a 
                href={CONTACT.facebook} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/30 hover:text-[#C9A86C] transition-colors duration-300" 
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a 
                href={CONTACT.instagram} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/30 hover:text-[#C9A86C] transition-colors duration-300" 
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.5} />
              </a>
              <a 
                href={CONTACT.linkedin} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-white/30 hover:text-[#C9A86C] transition-colors duration-300" 
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A86C] mb-6">
              {t.services_title}
            </h4>
            <ul className="space-y-3">
              {services.slice(0, 4).map((service, idx) => (
                <li key={idx}>
                  <span className="text-sm text-white/50 font-light">
                    {service.title} {service.subtitle}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A86C] mb-6">
              {t.company_title}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/portfolio" 
                  className="text-sm text-white/50 hover:text-[#C9A86C] transition-colors duration-300 font-light"
                >
                  {tNav.portfolio}
                </Link>
              </li>
              <li>
                <Link 
                  to="/knowledge" 
                  className="text-sm text-white/50 hover:text-[#C9A86C] transition-colors duration-300 font-light"
                >
                  {tNav.knowledge}
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-sm text-white/50 hover:text-[#C9A86C] transition-colors duration-300 font-light"
                >
                  {tNav.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A86C] mb-6">
              {t.contact_title}
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`mailto:${CONTACT.email}`} 
                  className="flex items-center gap-3 text-sm text-white/50 hover:text-[#C9A86C] transition-colors duration-300 font-light"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-[#C9A86C]/60" />
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${CONTACT.phone}`} 
                  className="flex items-center gap-3 text-sm text-white/50 hover:text-[#C9A86C] transition-colors duration-300 font-light"
                >
                  <Phone className="w-4 h-4 text-[#C9A86C]/60" />
                  {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <span className="flex items-center gap-3 text-sm text-white/50 font-light">
                  <MapPin className="w-4 h-4 text-[#C9A86C]/60" />
                  {CONTACT.location}
                </span>
              </li>
            </ul>
            
            {/* CTA */}
            <Link
              to="/contact"
              className="btn-primary mt-6 w-full justify-center"
              data-testid="footer-cta"
            >
              <span>{lang === 'pt' ? 'Agendar Vistoria' : 'Book Survey'}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-white/30 font-light">
            © {new Date().getFullYear()} Imperguel. {lang === 'pt' ? 'Todos os direitos reservados.' : 'All rights reserved.'}
          </p>
          <p className="text-xs text-white/30 font-light">
            {lang === 'pt' ? 'Impermeabilização e Isolamento Técnico' : 'Waterproofing and Technical Insulation'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
